import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  Check,
  ChevronsUpDown,
  Pencil,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import AiLoader from '../../EmbryoGradingAI/AiLoader';
import styles from './AIVoiceExtractionReview.module.css';

import type {
  ExtractionApiResponse,
  ExtractionReviewRouteState,
  ExtractionTypeOption,
} from './AIVoiceExtraction.types';
import {
  buildAiSummaryLines,
  convertTranscriptToExtraction,
  EXTRACTION_TYPE_OPTIONS,
  formatLabel,
  getStructuredExtractionData,
  hasMeaningfulValue,
} from './AIVoiceExtraction.api';
import {
  buildSummaryAiNotes,
  checkPatientHasActiveCycle,
  normalizeEmrDataForDocumentType,
  resolveDocumentType,
  saveSummaryNotes,
  saveVoiceExtraction,
  type VoiceDocumentType,
} from './AIVoiceExtraction.saveApi';

type SidebarMode = 'idle' | 'loading' | 'summary' | 'preview' | 'error';
type EditablePreviewMap = Record<string, string>;

type PreviewSectionItem = {
  kind: 'section';
  id: string;
  label: string;
  depth: number;
};

type PreviewFieldItem = {
  kind: 'field';
  id: string;
  label: string;
  value: string;
  isMultiline: boolean;
  showSelector: boolean;
  depth: number;
};

type PreviewRenderItem = PreviewSectionItem | PreviewFieldItem;

type PendingNoCycleFallback = {
  documentType: VoiceDocumentType;
  editedStructuredData: Record<string, unknown>;
};

const SELECT_STYLE_KEYS = [
  'patient',
  'name',
  'age',
  'duration',
  'doctor',
  'plan',
  'type',
  'protocol',
  'category',
  'stage',
  'grade',
  'method',
];

const showSuccessToast = (message: string) => {
  toast(
    <div className={styles.successToastContent}>
      <span className={styles.successToastIconWrap}>
        <Check size={16} strokeWidth={3} />
      </span>
      <span className={styles.successToastText}>{message}</span>
    </div>,
    {
      icon: false,
      closeButton: false,
      hideProgressBar: true,
      className: styles.successToastRoot,
      position: 'top-right',
      autoClose: 2600,
    }
  );
};

const showErrorToast = (message: string) => {
  toast(
    <div className={styles.errorToastContent}>
      <span className={styles.errorToastIconWrap}>
        <AlertCircle size={16} strokeWidth={3} />
      </span>
      <span className={styles.errorToastText}>{message}</span>
    </div>,
    {
      icon: false,
      closeButton: false,
      hideProgressBar: true,
      className: styles.errorToastRoot,
      position: 'top-right',
      autoClose: 3200,
    }
  );
};

const valueToText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';

  if (Array.isArray(value)) {
    const cleaned = value
      .filter((item) => hasMeaningfulValue(item))
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          const nestedText = Object.entries(item as Record<string, unknown>)
            .filter(([, nestedValue]) => hasMeaningfulValue(nestedValue))
            .map(
              ([nestedKey, nestedValue]) =>
                `${formatLabel(nestedKey)}: ${valueToText(nestedValue)}`
            )
            .join(', ');

          return nestedText;
        }

        return String(item);
      })
      .filter(Boolean);

    return cleaned.join(', ');
  }

  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, nestedValue]) => hasMeaningfulValue(nestedValue))
      .map(
        ([nestedKey, nestedValue]) =>
          `${formatLabel(nestedKey)}: ${valueToText(nestedValue)}`
      )
      .join(', ');
  }

  return String(value);
};

const shouldShowSelector = (label: string, value: string) => {
  const normalized = label.toLowerCase();
  return (
    value.length <= 30 &&
    SELECT_STYLE_KEYS.some((key) => normalized.includes(key))
  );
};

const isPrimitiveValue = (value: unknown) =>
  value === null || value === undefined || typeof value !== 'object';

const singularizeKey = (key: string) => {
  if (key.endsWith('ies')) return `${key.slice(0, -3)}y`;
  if (key.endsWith('s')) return key.slice(0, -1);
  return key;
};

const getArrayItemTitle = (arrayKey: string, item: unknown, index: number) => {
  if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
    const record = item as Record<string, unknown>;
    const preferredKeys = [
      'day',
      'embryo_number',
      'oocyte_number',
      'title',
      'name',
      'label',
      'cycle_number',
      'date',
    ];

    const matchedKey = preferredKeys.find((key) => {
      const candidate = record[key];
      return typeof candidate === 'string' && candidate.trim();
    });

    if (matchedKey) {
      return String(record[matchedKey]).trim();
    }
  }

  return `${formatLabel(singularizeKey(arrayKey))} ${index + 1}`;
};

const buildPreviewItems = (
  input: Record<string, unknown>,
  path = '',
  depth = 0
): PreviewRenderItem[] => {
  const items: PreviewRenderItem[] = [];

  Object.entries(input).forEach(([key, value]) => {
    if (!hasMeaningfulValue(value)) return;

    const currentPath = path ? `${path}.${key}` : key;
    const label = formatLabel(key);

    if (Array.isArray(value)) {
      const meaningfulItems = value.filter((item) => hasMeaningfulValue(item));
      if (!meaningfulItems.length) return;

      if (meaningfulItems.every((item) => isPrimitiveValue(item))) {
        const text = meaningfulItems.map((item) => String(item)).join(', ');

        items.push({
          kind: 'field',
          id: currentPath,
          label,
          value: text,
          isMultiline:
            text.length > 52 || text.includes(',') || text.includes('\n'),
          showSelector: shouldShowSelector(label, text),
          depth,
        });

        return;
      }

      items.push({
        kind: 'section',
        id: `${currentPath}.__section`,
        label,
        depth,
      });

      meaningfulItems.forEach((item, index) => {
        const itemPath = `${currentPath}[${index}]`;

        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
          items.push({
            kind: 'section',
            id: `${itemPath}.__section`,
            label: getArrayItemTitle(key, item, index),
            depth: depth + 1,
          });

          items.push(
            ...buildPreviewItems(
              item as Record<string, unknown>,
              itemPath,
              depth + 2
            )
          );

          return;
        }

        const text = valueToText(item);
        if (!text) return;

        items.push({
          kind: 'field',
          id: itemPath,
          label: getArrayItemTitle(key, item, index),
          value: text,
          isMultiline:
            text.length > 52 || text.includes(',') || text.includes('\n'),
          showSelector: false,
          depth: depth + 1,
        });
      });

      return;
    }

    if (typeof value === 'object' && value !== null) {
      const nestedEntries = Object.entries(value as Record<string, unknown>).filter(
        ([, nestedValue]) => hasMeaningfulValue(nestedValue)
      );

      if (!nestedEntries.length) return;

      items.push({
        kind: 'section',
        id: `${currentPath}.__section`,
        label,
        depth,
      });

      items.push(
        ...buildPreviewItems(
          value as Record<string, unknown>,
          currentPath,
          depth + 1
        )
      );

      return;
    }

    const text = String(value).trim();

    items.push({
      kind: 'field',
      id: currentPath,
      label,
      value: text,
      isMultiline: text.length > 52 || text.includes('\n') || text.includes(','),
      showSelector: shouldShowSelector(label, text),
      depth,
    });
  });

  return items;
};

const buildEditablePreviewValueMap = (
  items: PreviewRenderItem[]
): EditablePreviewMap => {
  return items.reduce<EditablePreviewMap>((acc, item) => {
    if (item.kind === 'field') {
      acc[item.id] = item.value;
    }
    return acc;
  }, {});
};

const parsePathTokens = (path: string): Array<string | number> => {
  const tokens: Array<string | number> = [];

  path.split('.').forEach((segment) => {
    const regex = /([^[\]]+)|\[(\d+)\]/g;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(segment)) !== null) {
      if (match[1]) tokens.push(match[1]);
      if (match[2]) tokens.push(Number(match[2]));
    }
  });

  return tokens;
};

const setValueAtPath = (
  target: Record<string, unknown> | unknown[],
  pathTokens: Array<string | number>,
  value: string
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current: any = target;

  pathTokens.forEach((token, index) => {
    const isLast = index === pathTokens.length - 1;

    if (isLast) {
      current[token] = value;
      return;
    }

    const nextToken = pathTokens[index + 1];
    const currentValue = current[token];

    const shouldCreateArray = typeof nextToken === 'number';
    const isValidExistingContainer = shouldCreateArray
      ? Array.isArray(currentValue)
      : typeof currentValue === 'object' &&
      currentValue !== null &&
      !Array.isArray(currentValue);

    if (!isValidExistingContainer) {
      current[token] = shouldCreateArray ? [] : {};
    }

    current = current[token];
  });
};

const applyEditedValuesToStructuredData = (
  structuredData: Record<string, unknown>,
  editedValues: EditablePreviewMap
): Record<string, unknown> => {
  const cloned = JSON.parse(
    JSON.stringify(structuredData)
  ) as Record<string, unknown>;

  Object.entries(editedValues).forEach(([fieldPath, value]) => {
    setValueAtPath(cloned, parsePathTokens(fieldPath), value);
  });

  return cloned;
};

const asNumber = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const numeric = Number(value.trim());
      if (!Number.isNaN(numeric)) {
        return numeric;
      }
    }
  }

  return null;
};

const AIVoiceExtractionReview: React.FC = () => {
  const location = useLocation();

  const navState = useMemo(
    () =>
      (location.state as ExtractionReviewRouteState | null) ??
      ({} as ExtractionReviewRouteState),
    [location.state]
  );

  const navStateRecord = navState as unknown as Record<string, unknown>;

  const [transcriptText, setTranscriptText] = useState(navState.transcript ?? '');
  const [isEditingTranscript, setIsEditingTranscript] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedType, setSelectedType] = useState<ExtractionTypeOption | null>(null);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('idle');
  const [extractionError, setExtractionError] = useState('');
  const [aiSummaryLines, setAiSummaryLines] = useState<string[]>([]);
  const [extractionResponse, setExtractionResponse] =
    useState<ExtractionApiResponse | null>(null);
  const [editablePreviewValues, setEditablePreviewValues] =
    useState<EditablePreviewMap>({});
  const [isSavingSummary, setIsSavingSummary] = useState(false);
  const [isSavingEmr, setIsSavingEmr] = useState(false);
  const [isCheckingCycle, setIsCheckingCycle] = useState(false);
  const [showNoActiveCycleModal, setShowNoActiveCycleModal] = useState(false);
  const [pendingNoCycleFallback, setPendingNoCycleFallback] =
    useState<PendingNoCycleFallback | null>(null);

  const extractionResponseRecord =
    (extractionResponse as unknown as Record<string, unknown> | null) ?? null;

  const filteredOptions = useMemo(() => {
    const value = searchValue.trim().toLowerCase();

    if (!value) return EXTRACTION_TYPE_OPTIONS;

    return EXTRACTION_TYPE_OPTIONS.filter(
      (option) =>
        option.label.toLowerCase().includes(value) ||
        option.description.toLowerCase().includes(value)
    );
  }, [searchValue]);

  const structuredExtractionData = useMemo<Record<string, unknown> | null>(() => {
    if (!extractionResponse) return null;
    return getStructuredExtractionData(extractionResponse) as Record<string, unknown>;
  }, [extractionResponse]);

  const basePreviewItems = useMemo(() => {
    if (!structuredExtractionData) return [];
    return buildPreviewItems(structuredExtractionData);
  }, [structuredExtractionData]);

  const previewItems = useMemo(() => {
    return basePreviewItems.map((item) =>
      item.kind === 'field'
        ? {
          ...item,
          value: editablePreviewValues[item.id] ?? item.value,
        }
        : item
    );
  }, [basePreviewItems, editablePreviewValues]);

  const summaryHeading = useMemo(() => {
    if (!selectedType) return 'AI Summary';
    return `${selectedType.label} AI Summary`;
  }, [selectedType]);

  const selectedDocumentType = useMemo(
    () => resolveDocumentType(selectedType),
    [selectedType]
  );

  const resolvedPatientId = useMemo(() => {
    return asNumber(
      navState.patientId,
      navStateRecord.patient_id,
      extractionResponseRecord?.patientId,
      extractionResponseRecord?.patient_id
    );
  }, [navState, navStateRecord, extractionResponseRecord]);

  const resolvedTranscriptionId = useMemo(() => {
    return asNumber(
      navStateRecord.transcriptionId,
      navStateRecord.transcription_id,
      extractionResponseRecord?.transcriptionId,
      extractionResponseRecord?.transcription_id
    );
  }, [navStateRecord, extractionResponseRecord]);

  const resolvedEmrId = useMemo(() => {
    return asNumber(
      navStateRecord.emrId,
      navStateRecord.emr_id,
      extractionResponseRecord?.emrId,
      extractionResponseRecord?.emr_id
    );
  }, [navStateRecord, extractionResponseRecord]);

  const isCycleRequiredDocumentType =
    selectedDocumentType === 'embryo_grading' ||
    selectedDocumentType === 'oocyte_grading';

  const runExtraction = async (option: ExtractionTypeOption) => {
    if (!transcriptText.trim()) {
      setExtractionError(
        'Transcript is empty. Please add transcript text before extracting.'
      );
      setSidebarMode('error');
      return;
    }

    setSelectedType(option);
    setSidebarMode('loading');
    setExtractionError('');
    setExtractionResponse(null);
    setAiSummaryLines([]);
    setEditablePreviewValues({});
    setPendingNoCycleFallback(null);
    setShowNoActiveCycleModal(false);

    try {
      const response = await convertTranscriptToExtraction(transcriptText, option);
      const summaryLines = buildAiSummaryLines(response, option);
      const structuredData = getStructuredExtractionData(response) as Record<
        string,
        unknown
      >;
      const previewStructure = buildPreviewItems(structuredData);

      setExtractionResponse(response);
      setAiSummaryLines(summaryLines);
      setEditablePreviewValues(buildEditablePreviewValueMap(previewStructure));
      setSidebarMode('summary');
    } catch (error) {
      setExtractionError(
        error instanceof Error ? error.message : 'Failed to extract data from transcript.'
      );
      setSidebarMode('error');
    }
  };

  const resetToSelection = () => {
    setSidebarMode('idle');
    setExtractionError('');
    setExtractionResponse(null);
    setAiSummaryLines([]);
    setSelectedType(null);
    setEditablePreviewValues({});
    setPendingNoCycleFallback(null);
    setShowNoActiveCycleModal(false);
  };

  const handleRetry = async () => {
    if (!selectedType) {
      resetToSelection();
      return;
    }

    await runExtraction(selectedType);
  };

  const handleViewExtractedData = () => {
    if (!extractionResponse) return;
    setSidebarMode('preview');
  };

  const handleBackToSummary = () => {
    setSidebarMode('summary');
  };

  const handlePreviewFieldChange = (fieldId: string, value: string) => {
    setEditablePreviewValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleCloseNoActiveCycleModal = () => {
    if (isSavingSummary) return;
    setShowNoActiveCycleModal(false);
    setPendingNoCycleFallback(null);
  };

  const handleSaveSummaryToNotes = async () => {
    if (!selectedType || !selectedDocumentType || !extractionResponse) {
      showErrorToast('No AI extraction result is available to save.');
      return;
    }

    if (!resolvedPatientId) {
      showErrorToast(
        'Patient ID is missing. Pass patientId in route state before saving.'
      );
      return;
    }

    try {
      setIsSavingSummary(true);

      const aiNotes = buildSummaryAiNotes({
        option: selectedType,
        documentType: selectedDocumentType,
        summaryHeading,
        summaryLines: aiSummaryLines,
        extractionResponse,
        transcriptionId: resolvedTranscriptionId,
        emrId: resolvedEmrId,
        saveContext: 'manual_summary_save',
        cycleValidation: {
          checked: false,
          hasActiveCycle: null,
        },
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const getEmbryologistName = (data: any): string => {
        const cultureDays = data?.embryo_culture?.culture_days;

        if (!Array.isArray(cultureDays)) return '';

        // Find first non-empty embryologist
        const found = cultureDays.find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (day: any) => day?.embryologist && day.embryologist.trim() !== ''
        );
        return found?.embryologist ?? '';
      };

      if (String(selectedDocumentType) === 'embryo_grading') {
        const doctorName = getEmbryologistName(extractionResponse.emr_data);
        await saveSummaryNotes({
          patientId: resolvedPatientId,
          aiNotes,
          duration: '0',
          type: 'voice_to_text',
          document_name: '',
          document_type: selectedDocumentType ?? '',
          doctor_name: doctorName,
          source: selectedType?.label ?? '',
          call_trascript: transcriptText,
        });
      } else if (String(selectedDocumentType) === 'oocyte_grading') {
        await saveSummaryNotes({
          patientId: resolvedPatientId,
          aiNotes,
          duration: '0',
          type: 'voice_to_text',
          document_name: '',
          document_type: selectedDocumentType ?? '',
          doctor_name: (extractionResponse.emr_data?.oocyte_grading as Record<string, unknown>)?.embryologist as string | undefined ?? '',
          source: selectedType?.label ?? '',
          call_trascript: transcriptText,
        });
      } else {
        await saveSummaryNotes({
          patientId: resolvedPatientId,
          aiNotes,
          duration: '0',
          type: 'voice_to_text',
          document_name: '',
          document_type: selectedDocumentType ?? '',
          doctor_name: (extractionResponse.emr_data?.general_info as Record<string, unknown>)?.referring_doctor as string | undefined ?? '',
          source: selectedType?.label ?? '',
          call_trascript: transcriptText,
        });
      }

      showSuccessToast('Summary Saved Successfully!');
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Failed to save AI summary.'
      );
    } finally {
      setIsSavingSummary(false);
    }
  };

  const handleSaveFallbackSummaryFromModal = async () => {
    if (
      !selectedType ||
      !selectedDocumentType ||
      !extractionResponse ||
      !resolvedPatientId ||
      !pendingNoCycleFallback
    ) {
      showErrorToast('Unable to save fallback summary.');
      return;
    }

    try {
      setIsSavingSummary(true);

      const aiNotes = buildSummaryAiNotes({
        option: selectedType,
        documentType: selectedDocumentType,
        summaryHeading,
        summaryLines: aiSummaryLines,
        extractionResponse,
        transcriptionId: resolvedTranscriptionId,
        emrId: resolvedEmrId,
        editedStructuredData: pendingNoCycleFallback.editedStructuredData,
        saveContext: 'no_active_cycle_fallback',
        cycleValidation: {
          checked: true,
          hasActiveCycle: false,
        },
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const getEmbryologistName = (data: any): string => {
        const cultureDays = data?.embryo_culture?.culture_days;

        if (!Array.isArray(cultureDays)) return '';

        // Find first non-empty embryologist
        const found = cultureDays.find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (day: any) => day?.embryologist && day.embryologist.trim() !== ''
        );

        return found?.embryologist ?? '';
      };

      if (String(selectedDocumentType) === 'embryo_grading') {
        const doctorName = getEmbryologistName(extractionResponse.emr_data);
        await saveSummaryNotes({
          patientId: resolvedPatientId,
          aiNotes,
          duration: '0',
          type: 'voice_to_text',
          document_name: '',
          document_type: selectedDocumentType ?? '',
          doctor_name: doctorName,
          source: selectedType?.label ?? '',
          call_trascript: transcriptText,
        });
      } else if (String(selectedDocumentType) === 'oocyte_grading') {
        await saveSummaryNotes({
          patientId: resolvedPatientId,
          aiNotes,
          duration: '0',
          type: 'voice_to_text',
          document_name: '',
          document_type: selectedDocumentType ?? '',
          doctor_name: (extractionResponse.emr_data?.oocyte_grading as Record<string, unknown>)?.embryologist as string | undefined ?? '',
          source: selectedType?.label ?? '',
          call_trascript: transcriptText,
        });
      } else {
        await saveSummaryNotes({
          patientId: resolvedPatientId,
          aiNotes,
          duration: '0',
          type: 'voice_to_text',
          document_name: '',
          document_type: selectedDocumentType ?? '',
          doctor_name: (extractionResponse.emr_data?.general_info as Record<string, unknown>)?.referring_doctor as string | undefined ?? '',
          source: selectedType?.label ?? '',
          call_trascript: transcriptText,
        });
      }

      setShowNoActiveCycleModal(false);
      setPendingNoCycleFallback(null);
      showSuccessToast('Saved to Summary Successfully!');
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Failed to save fallback summary.'
      );
    } finally {
      setIsSavingSummary(false);
    }
  };

  const handleSaveToEMR = async () => {
    if (!selectedType || !selectedDocumentType || !structuredExtractionData) {
      showErrorToast('No structured extraction data is available to save.');
      return;
    }

    if (!resolvedPatientId) {
      showErrorToast(
        'Patient ID is missing. Pass patientId in route state before saving.'
      );
      return;
    }

    if (!resolvedTranscriptionId) {
      showErrorToast(
        'transcription_id is missing. Pass transcriptionId/transcription_id in route state or parser response.'
      );
      return;
    }

    if (!resolvedEmrId) {
      showErrorToast(
        'emr_id is missing. Pass emrId/emr_id in route state or parser response.'
      );
      return;
    }

    try {
      setIsSavingEmr(true);

      const editedStructuredData = applyEditedValuesToStructuredData(
        structuredExtractionData,
        editablePreviewValues
      );

      if (isCycleRequiredDocumentType) {
        setIsCheckingCycle(true);

        const cycleCheck = await checkPatientHasActiveCycle(resolvedPatientId);

        if (!cycleCheck.hasActiveCycle) {
          setPendingNoCycleFallback({
            documentType: selectedDocumentType,
            editedStructuredData,
          });
          setShowNoActiveCycleModal(true);
          return;
        }
      }

      const normalizedEmrData = normalizeEmrDataForDocumentType(
        selectedDocumentType,
        editedStructuredData
      );

      await saveVoiceExtraction({
        patientId: resolvedPatientId,
        transcriptionId: resolvedTranscriptionId,
        emrId: resolvedEmrId,
        documentType: selectedDocumentType,
        emrData: normalizedEmrData,
      });

      showSuccessToast('Data Saved to EMR Successfully!');
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Failed to save extracted EMR data.'
      );
    } finally {
      setIsSavingEmr(false);
      setIsCheckingCycle(false);
    }
  };

  return (
    <div className={styles.pageShell}>
      <div className={styles.headerRow}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>AI Voice Data Entry</h1>

          {/* <div className={styles.iconActions}>
            <button className={styles.iconButton} aria-label="Edit" type="button">
              <Pencil size={16} />
            </button>
            <button className={styles.iconButton} aria-label="Open" type="button">
              <ExternalLink size={16} />
            </button>
          </div> */}
        </div>
      </div>

      <div className={styles.contentLayout}>
        <section className={styles.transcriptPanel}>
          <textarea
            value={transcriptText}
            onChange={(e) => setTranscriptText(e.target.value)}
            readOnly={!isEditingTranscript}
            className={`${styles.transcriptArea} ${isEditingTranscript ? styles.transcriptEditable : ''}`}
            placeholder="Transcript will appear here..."
          />

          <div className={styles.transcriptFooter}>
            <button
              type="button"
              className={styles.editTranscriptButton}
              onClick={() => setIsEditingTranscript((prev) => !prev)}
            >
              <Pencil size={18} />
              {isEditingTranscript ? 'Save Transcript' : 'Edit Transcript'}
            </button>
          </div>
        </section>

        <aside className={styles.sidebarPanel}>
          <div className={styles.sidebarHeader}>
            <h2 className={styles.sidebarTitle}>AI Clinical Extraction</h2>
          </div>

          <div className={styles.sidebarBody}>
            {sidebarMode === 'idle' && (
              <div className={styles.idleState}>
                <div className={styles.welcomeState}>
                  <div className={styles.welcomeOrbWrap}>
                    <div className={styles.welcomeLoaderOverlay}>
                      <AiLoader />
                    </div>
                  </div>

                  <h3 className={styles.welcomeTitle}>Welcome to AI Extraction</h3>
                  <p className={styles.welcomeText}>
                    Please search and select a page from below to run an AI scan and extract data.
                  </p>
                </div>

                <div className={styles.searchWrap}>
                  <Search size={18} className={styles.searchIcon} />
                  <input
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Search EMR page"
                    className={styles.searchInput}
                  />
                </div>

                <div className={styles.recommendedLabel}>Recommended Pages :</div>

                <div className={styles.chipsWrap}>
                  {filteredOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      className={styles.pageChip}
                      onClick={() => void runExtraction(option)}
                      title={option.description}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {extractionError && <div className={styles.errorBox}>{extractionError}</div>}
              </div>
            )}

            {sidebarMode === 'loading' && (
              <div className={styles.loadingOnlyState}>
                <div className={styles.loadingOnlyOrbWrap}>
                  <div className={styles.loadingOnlyLoaderOverlay}>
                    <AiLoader />
                  </div>
                </div>

                <h3 className={styles.loadingTitle}>Extracting Clinical Data</h3>
                <p className={styles.loadingText}>
                  Running AI extraction for{' '}
                  <span className={styles.highlightText}>
                    {selectedType?.label ?? 'selected page'}
                  </span>
                  . Please wait while we analyze the transcript.
                </p>
              </div>
            )}

            {sidebarMode === 'summary' && extractionResponse && (
              <div className={styles.summaryState}>
                <div className={styles.summaryScrollArea}>
                  <div className={styles.summaryDesignCard}>
                    <h3 className={styles.summaryDesignTitle}>{summaryHeading}</h3>

                    {aiSummaryLines.length ? (
                      <ul className={styles.summaryBulletList}>
                        {aiSummaryLines.map((line, index) => (
                          <li key={`${line}-${index}`} className={styles.summaryBulletItem}>
                            {line}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className={styles.summaryEmpty}>
                        No summary content was returned by the API.
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.summaryFooter}>
                  <div className={styles.summaryButtonRow}>
                    <button
                      type="button"
                      className={styles.scanAgainButton}
                      onClick={resetToSelection}
                      disabled={isSavingSummary || isSavingEmr || isCheckingCycle}
                    >
                      <Sparkles size={16} />
                      <span>Scan Again</span>
                    </button>

                    <button
                      type="button"
                      className={styles.primaryDarkButton}
                      onClick={handleViewExtractedData}
                      disabled={isSavingSummary || isSavingEmr || isCheckingCycle}
                    >
                      Extract to Fields
                    </button>
                  </div>

                  <button
                    type="button"
                    className={styles.fullWidthSecondaryButton}
                    onClick={() => void handleSaveSummaryToNotes()}
                    disabled={isSavingSummary || isSavingEmr || isCheckingCycle}
                  >
                    {isSavingSummary ? 'Saving Summary...' : 'Save Summary to Notes'}
                  </button>
                </div>
              </div>
            )}

            {sidebarMode === 'preview' && extractionResponse && (
              <div className={styles.previewState}>
                <div className={styles.previewScrollArea}>
                  {previewItems.length ? (
                    <div className={styles.previewStructuredStack}>
                      {previewItems.map((item) => {
                        if (item.kind === 'section') {
                          const indent = Math.min(item.depth, 4) * 10;

                          return (
                            <div
                              key={item.id}
                              className={styles.previewSectionBlock}
                              style={{ marginLeft: `${indent}px` }}
                            >
                              <div
                                className={`${styles.previewSectionTitle} ${item.depth === 0
                                  ? styles.previewSectionTitlePrimary
                                  : styles.previewSectionTitleSecondary
                                }`}
                              >
                                {item.label}
                              </div>
                            </div>
                          );
                        }

                        const indent = Math.min(item.depth, 4) * 10;

                        return (
                          <div
                            key={item.id}
                            className={styles.previewFieldBlock}
                            style={{ marginLeft: `${indent}px` }}
                          >
                            <label className={styles.previewFieldLabel}>
                              {item.label}
                            </label>

                            <div
                              className={`${styles.previewFieldBox} ${item.isMultiline ? styles.previewFieldBoxMultiline : ''
                              }`}
                            >
                              {item.isMultiline ? (
                                <textarea
                                  className={styles.previewFieldTextarea}
                                  value={item.value}
                                  rows={4}
                                  onChange={(e) =>
                                    handlePreviewFieldChange(item.id, e.target.value)
                                  }
                                />
                              ) : (
                                <>
                                  <input
                                    className={styles.previewFieldInput}
                                    value={item.value}
                                    onChange={(e) =>
                                      handlePreviewFieldChange(item.id, e.target.value)
                                    }
                                  />

                                  {item.showSelector && (
                                    <span className={styles.previewFieldIcon}>
                                      <ChevronsUpDown size={16} />
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={styles.previewEmptyState}>
                      No structured preview data was returned by the API.
                    </div>
                  )}
                </div>

                <div className={styles.previewFooter}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={handleBackToSummary}
                    disabled={isSavingEmr || isCheckingCycle}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className={styles.primaryDarkButton}
                    onClick={() => void handleSaveToEMR()}
                    disabled={isSavingEmr || isCheckingCycle}
                  >
                    {isCheckingCycle
                      ? 'Checking Cycle...'
                      : isSavingEmr
                        ? 'Saving to EMR...'
                        : 'Save to EMR'}
                  </button>
                </div>
              </div>
            )}

            {sidebarMode === 'error' && (
              <div className={styles.errorState}>
                <div className={styles.loadingOnlyOrbWrap}>
                  <div className={styles.loadingOnlyLoaderOverlay}>
                    <AiLoader />
                  </div>
                </div>

                <h3 className={styles.errorTitle}>Extraction Failed</h3>
                <div className={styles.errorBox}>{extractionError}</div>

                <div className={styles.errorActions}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={resetToSelection}
                  >
                    Back
                  </button>

                  <button
                    type="button"
                    className={styles.primaryDarkButton}
                    onClick={() => void handleRetry()}
                  >
                    Retry Extraction
                  </button>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>

      {showNoActiveCycleModal && (
        <div className={styles.noCycleModalBackdrop}>
          <div
            className={styles.noCycleModalCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="no-cycle-modal-title"
          >
            <div className={styles.noCycleModalHeader}>
              <h3 id="no-cycle-modal-title" className={styles.noCycleModalTitle}>
                No Active Cycle Found
              </h3>

              <button
                type="button"
                className={styles.noCycleModalCloseButton}
                onClick={handleCloseNoActiveCycleModal}
                disabled={isSavingSummary}
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </div>

            <div className={styles.noCycleModalDivider} />

            <div className={styles.noCycleModalBody}>
              <div className={styles.noCycleModalHighlightBox}>
                <span className={styles.noCycleModalFloatingLabel}>
                  Cycle Validation
                </span>
                <div className={styles.noCycleModalHighlightValue}>
                  This patient does not have any active cycle.
                </div>
              </div>

              <p className={styles.noCycleModalMessage}>
                {selectedType?.label ?? 'This extraction'} can be saved to EMR only
                when an active cycle exists. You can save the extracted data
                to AI Summary instead.
              </p>
            </div>

            <div className={styles.noCycleModalActions}>
              <button
                type="button"
                className={styles.noCycleModalSecondaryButton}
                onClick={handleCloseNoActiveCycleModal}
                disabled={isSavingSummary}
              >
                Cancel
              </button>

              <button
                type="button"
                className={styles.noCycleModalPrimaryButton}
                onClick={() => void handleSaveFallbackSummaryFromModal()}
                disabled={isSavingSummary}
              >
                {isSavingSummary ? 'Saving...' : 'Save to Summary'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIVoiceExtractionReview;